import React, { useState, useEffect } from 'react'
import {
  CreditCardIcon,
  UsersIcon,
  ChartBarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { AdminHeader } from '../../../shared/components'
import PlanEditor from '../components/PlanEditor'
import PlanPreview from '../components/PlanPreview'
import { Plan, PlanStats } from '../types/plan.types'

const PlanManagementPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([])
  const [stats, setStats] = useState<PlanStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [showPlanEditor, setShowPlanEditor] = useState(false)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)
  const [deletingPlan, setDeletingPlan] = useState<string | null>(null)
  const [showPlanPreview, setShowPlanPreview] = useState(false)
  const [previewingPlan, setPreviewingPlan] = useState<Plan | null>(null)

  useEffect(() => {
    fetchPlans()
    fetchStats()
  }, [])

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/admin/plans')
      if (response.ok) {
        const data = await response.json()
        setPlans(data.plans)
      }
    } catch {
      toast.error('Failed to fetch plans')
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/plans/stats')
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch {
      toast.error('Failed to fetch statistics')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setLoading(true)
    await Promise.all([fetchPlans(), fetchStats()])
    setLoading(false)
    toast.success('Data refreshed successfully')
  }

  const handleCreatePlan = () => {
    setEditingPlan(null)
    setShowPlanEditor(true)
  }

  const handleEditPlan = (plan: Plan) => {
    setEditingPlan(plan)
    setShowPlanEditor(true)
  }

  const handleViewPlan = (plan: Plan) => {
    setPreviewingPlan(plan)
    setShowPlanPreview(true)
  }

  const handleSavePlan = (savedPlan: Plan) => {
    if (editingPlan) {
      // Update existing plan
      setPlans(prev => prev.map(p => (p._id === savedPlan._id ? savedPlan : p)))
    } else {
      // Add new plan
      setPlans(prev => [savedPlan, ...prev])
    }
    fetchStats() // Refresh stats
  }

  const handleDeletePlan = async (planId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this plan? This action cannot be undone.'
      )
    ) {
      return
    }

    setDeletingPlan(planId)
    try {
      const response = await fetch(`/api/admin/plans/${planId}`, {
        method: 'DELETE',
      })

      const data = await response.json()

      if (data.success) {
        setPlans(prev => prev.filter(p => p._id !== planId))
        toast.success('Plan deleted successfully')
        fetchStats() // Refresh stats
      } else {
        toast.error(data.message || 'Failed to delete plan')
      }
    } catch {
      toast.error('Failed to delete plan')
    } finally {
      setDeletingPlan(null)
    }
  }

  const handleTogglePlanStatus = async (planId: string, isActive: boolean) => {
    try {
      const response = await fetch(`/api/admin/plans/${planId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !isActive }),
      })

      const data = await response.json()

      if (data.success) {
        setPlans(prev =>
          prev.map(p => (p._id === planId ? { ...p, isActive: !isActive } : p))
        )
        toast.success(
          `Plan ${!isActive ? 'activated' : 'deactivated'} successfully`
        )
      } else {
        toast.error(data.message || 'Failed to update plan status')
      }
    } catch {
      toast.error('Failed to update plan status')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <AdminHeader
        title="Plan Management"
        subtitle="Manage subscription plans, pricing, and user subscriptions"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={loading}
        lastUpdated={new Date()}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Actions */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Plans Overview
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Create and manage subscription plans for your service
            </p>
          </div>
          <button
            onClick={handleCreatePlan}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            Create Plan
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Stats Cards */}
        {!loading && stats && (
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-4">
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <div className="flex items-center">
                <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900">
                  <CreditCardIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Plans
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalPlans}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <div className="flex items-center">
                <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900">
                  <UsersIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <div className="flex items-center">
                <div className="rounded-lg bg-yellow-100 p-2 dark:bg-yellow-900">
                  <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Monthly Revenue
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₩{stats.monthlyRevenue}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <div className="flex items-center">
                <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900">
                  <ChartBarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Revenue
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₩{stats.totalRevenue}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans Table */}
        {!loading && (
          <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Plans
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Pricing
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Features
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {plans.map(plan => (
                    <tr key={plan._id}>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          {plan.color && (
                            <div
                              className="mr-3 h-4 w-4 rounded-full"
                              style={{ backgroundColor: plan.color }}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {plan.displayName}
                              {plan.badge && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                  {plan.badge}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {plan.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div>₩{plan.price.monthly}/month</div>
                          <div className="text-gray-500 dark:text-gray-400">
                            ₩{plan.price.yearly}/year
                            {plan.price.yearlyDiscount && (
                              <span className="ml-1 text-green-600">
                                ({plan.price.yearlyDiscount}% off)
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div>
                            Links:{' '}
                            {plan.features.linksPerMonth === -1
                              ? 'Unlimited'
                              : plan.features.linksPerMonth}
                          </div>
                          <div>
                            Domains:{' '}
                            {plan.features.customDomains === -1
                              ? 'Unlimited'
                              : plan.features.customDomains}
                          </div>
                          <div>
                            Team:{' '}
                            {plan.features.teamMembers === -1
                              ? 'Unlimited'
                              : plan.features.teamMembers}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <button
                            onClick={() =>
                              plan._id &&
                              handleTogglePlanStatus(plan._id, plan.isActive)
                            }
                            className="mr-2"
                            disabled={!plan._id}
                          >
                            {plan.isActive ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </button>
                          <span
                            className={`text-sm ${plan.isActive ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditPlan(plan)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Edit Plan"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewPlan(plan)}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              plan._id && handleDeletePlan(plan._id)
                            }
                            disabled={deletingPlan === plan._id || !plan._id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete Plan"
                          >
                            {deletingPlan === plan._id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-red-600"></div>
                            ) : (
                              <TrashIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && plans.length === 0 && (
          <div className="py-12 text-center">
            <CreditCardIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
              No plans found
            </h3>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              Get started by creating your first subscription plan.
            </p>
            <button
              onClick={handleCreatePlan}
              className="mx-auto flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              <PlusIcon className="h-5 w-5" />
              Create Your First Plan
            </button>
          </div>
        )}
      </div>

      {/* Plan Editor Modal */}
      <PlanEditor
        isOpen={showPlanEditor}
        onClose={() => {
          setShowPlanEditor(false)
          setEditingPlan(null)
        }}
        plan={editingPlan}
        onSave={handleSavePlan}
      />

      {/* Plan Preview Modal */}
      <PlanPreview
        isOpen={showPlanPreview}
        onClose={() => {
          setShowPlanPreview(false)
          setPreviewingPlan(null)
        }}
        plan={previewingPlan}
      />
    </div>
  )
}

export default PlanManagementPage
